import { useEffect, useState } from 'react'
import { format, parseISO, isValid } from 'date-fns'
import { ru } from 'date-fns/locale'
import { ProfileLayout } from '@/components/layout/ProfileLayout'
import { useTranslation } from 'react-i18next'
import { mergeSubscriptionParameters } from '@/api/auth'
import { useAuthStore } from '@/stores/authStore'
import { useUserStore } from '@/stores/userStore'
import { Box, Card, CardContent, IconButton, Tooltip, Typography, Avatar } from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { ProfileEditDialog } from '@/components/profile/ProfileEditDialog'
import { StudentFormSummary } from '@/components/studentForm/StudentFormSummary'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { usersApi } from '@/api/users'
import '@/styles/dashboard.css'

export function ProfilePage() {
  const { t, i18n } = useTranslation()
  const authUser = useAuthStore((state) => state.user)
  const setAuthUser = useAuthStore((state) => state.setUser)
  const { currentUser, fetchUser, setCurrentUser } = useUserStore()
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [contractId, setContractId] = useState<number | null>(null)

  useEffect(() => {
    if (authUser?.id) {
      fetchUser(authUser.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.id])

  const isStudent = authUser?.roles?.some((r) => r.slug === 'student') ?? false

  // Анкета живёт на договоре с учителем — своего договора у ученика в профиле нет
  useEffect(() => {
    if (!isStudent) {
      setContractId(null)
      return
    }

    let cancelled = false
    void usersApi
      .getTeacher()
      .then((data) => {
        if (!cancelled) setContractId(data.contract?.id ?? null)
      })
      .catch(() => {
        if (!cancelled) setContractId(null)
      })

    return () => {
      cancelled = true
    }
  }, [isStudent])

  const user = currentUser || authUser

  const formatBirthday = (value: string | null | undefined): string => {
    if (!value) return '-'
    try {
      const date = parseISO(value)
      const locale = i18n.language === 'ru' ? ru : undefined
      return isValid(date) ? format(date, 'dd.MM.yyyy', { locale }) : value
    } catch {
      return value
    }
  }

  const getInitials = () => {
    if (!user) return '?'
    const parts = [user.surname, user.first_name].filter(Boolean)
    if (parts.length) {
      return parts
        .join(' ')
        .split(' ')
        .filter(Boolean)
        .map((p) => p[0]?.toUpperCase())
        .join('')
        .slice(0, 2)
    }
    if (user.email) return user.email[0]?.toUpperCase()
    return '?'
  }

  return (
    <ProfileLayout>
      <Box sx={{ p: 3, pt: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {t('profile.title')}
          </Typography>
          <Tooltip title={t('profile.edit')}>
            <IconButton
              size="small"
              onClick={() => setIsEditDialogOpen(true)}
              aria-label={t('profile.edit')}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <PageHelpPopover pageKey="profile" />
        </Box>

        <Card>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                gap: 4,
                alignItems: 'flex-start',
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Avatar
                src={user?.avatar_url ?? undefined}
                sx={{
                  width: { xs: 120, sm: 160 },
                  height: { xs: 120, sm: 160 },
                  fontSize: { xs: '2.5rem', sm: '3rem' },
                }}
              >
                {getInitials()}
              </Avatar>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: { xs: 0, sm: 0.5 } }}>
                  {t('profile.personalInfo')}
                </Typography>

                <Box sx={{ mt: 2 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('profile.email')}
                    </Typography>
                    <Typography variant="body1">{user?.email || '-'}</Typography>
                  </Box>

              {user?.first_name && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('profile.firstName')}
                  </Typography>
                  <Typography variant="body1">
                    {user.first_name}
                  </Typography>
                </Box>
              )}

              {user?.surname && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('profile.surname')}
                  </Typography>
                  <Typography variant="body1">
                    {user.surname}
                  </Typography>
                </Box>
              )}

              {user?.middle_name && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('profile.middleName')}
                  </Typography>
                  <Typography variant="body1">
                    {user.middle_name}
                  </Typography>
                </Box>
              )}

              {user?.name && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('profile.name')}
                  </Typography>
                  <Typography variant="body1">{user.name}</Typography>
                </Box>
              )}

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('profile.birthDate')}
                </Typography>
                <Typography variant="body1">
                  {formatBirthday(user?.birthday ?? null)}
                </Typography>
              </Box>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Анкета ученика — отдельным блоком под основной информацией */}
        {contractId !== null && user != null && (
          <StudentFormSummary contractId={contractId} userId={user.id} canManage={false} />
        )}

        {user && (
          <ProfileEditDialog
            open={isEditDialogOpen}
            onClose={() => setIsEditDialogOpen(false)}
            user={user}
            onSaved={(updated) => {
              void (async () => {
                setCurrentUser(updated)
                if (authUser && authUser.id === updated.id) {
                  const merged = await mergeSubscriptionParameters(updated)
                  setAuthUser(merged)
                }
                // На практике полезно перечитать пользователя после multipart-обновления,
                // чтобы UI гарантированно показал реально сохраненные поля (avatar/birthday).
                fetchUser(updated.id).catch(() => {})
              })()
            }}
          />
        )}
      </Box>
    </ProfileLayout>
  )
}
